/**
 * TODO:
 * 現状の形だと、jsonもバンドルに含まれることで、ユーザはjsを調べることで他のブランド/タウンの設定値も知ることができる.
 * 現時点では特に機微情報も含まないため一旦はこのままにしておくが、将来的にはビルド時に必要なタウンの設定だけを取り込む形に修正する.
 *
 * 機微情報あるいはそれに準ずる内容が必要になった場合は、直ちに↑の対応が必要.
 *
 * https://github.com/my-color/front/pull/612#pullrequestreview-206263182
 */
import RawBehaviorConfig from '@/config/app/behavior.json'
import ApiBrand from '@/models/api/brand'
import { create as createCurrentTime } from '@/models/current-time/functions'
import ShippingFee from '@/models/shipping-fee'
import AppId from '@/util/appid'
import _ from 'lodash'
import Company from './attributes/company'

import BundleRecommendation from './config/bundle-recommendation'
import Cart from './config/cart/cart'
import { ColorFamily } from './config/color-family'
import { Discount } from './config/discount'
import { BrandShippingFeeBuilder } from './config/fix-shipping-fee'
import Footer from './config/footer'
import { Item } from './config/item'
import { ItemDetail } from './config/item-detail'
import ItemList from './config/item-list'
import Layer from './config/layer'
import { OrderHistory } from './config/order-history'
import { PaidMembership } from './config/paid-membership'
import ReviewSection from './config/review-section'
import { ShipmentSuspendNotificationConfig } from './config/shipment-suspend-notification'
import SideBar from './config/side-bar'
import SlideMenu from './config/slide-menu'
import { BrandEntity, BrandGroupEntity, Config, FilterIsExclusive, RawConfig } from './types'

export default class BehaviorConfig {
  static createFromBrand(brand: ApiBrand | string | null): BehaviorConfig {
    const brandIdentifier = brand instanceof ApiBrand ? brand.englishName : brand
    const brandAppId = AppId.getByBrandName(brandIdentifier)

    return new BehaviorConfig(
      brandAppId,
      RawBehaviorConfig as any,
      brandIdentifier
    )
  }
  static get forHost(): BehaviorConfig {
    return new BehaviorConfig(
      AppId.getHostId(),
      RawBehaviorConfig as any,
      null
    )
  }

  constructor(
    private readonly brandAppId: string | null = null,
    private readonly json: RawConfig = RawBehaviorConfig as any,
    private readonly brandIdentifier: string | null = null
  ) { }

  get groupId(): string {
    return this.getGroupAttribute('id')
  }

  get groupLabel(): string {
    return this.getGroupAttribute('label')
  }

  get company(): Company {
    return new Company(this.getGroupAttribute('company'))
  }

  /* is{ホスト名}系は、利用側が直接AppIdモジュールを参照すれば、一通り不要？ */

  get isMyColor(): boolean {
    return this.hostId === AppId.getByHostName('mycolor')
  }

  /**
   * <p>
   * 美ラボとホテラバは機能的に同等とするため、同一の設定・振る舞いを流用すべき箇所がある.
   * そのケースに該当する箇所で「ホテラバのように振る舞うべきか？」という判断を行うためにこのプロパティを使用すること.
   * </p>
   * <p>
   * ホテラバと美ラボで明確に区別が必要な箇所でこのプロパティを使用してはならない.
   * どの部分が同一であるべきで、どの部分が区別されるべきかは機能や内容に応じて判断する必要がある.
   * </p>
   * <p>
   * このようなプロパティを定義したのは「美ラボはホテラバにとってのカナリア的立ち位置である」という特殊さから作成したもの.
   * 例え複数のサービスが類似の振る舞いを取ることが多くなったとしても、同様の isLikeXXX プロパティは<b>原則として追加しないこと</b>.
   * </p>
   *
   * @see https://github.com/my-color/front/issues/7343
   */
  get isLikeHotellovers(): boolean {
    return this.isVilb || this.isHotellovers
  }
  get isVilb(): boolean {
    /**
     * {@link hostAppIdMap}のコメントも参照
     */
    return this.hostId === AppId.getByHostName('hl')
  }
  get isHotellovers(): boolean {
    /**
     * {@link hostAppIdMap}のコメントも参照
     */
    return this.hostId === AppId.getByHostName('hl2')
  }

  get isFitowa(): boolean {
    return this.hostId === AppId.getByHostName('fitowa')
  }

  get isLedian(): boolean {
    return this.hostId === AppId.getByHostName('ledian')
  }

  get slideMenu(): SlideMenu {
    return new SlideMenu(this.get('slideMenu'))
  }

  get reviewSection(): ReviewSection {
    return new ReviewSection(this.get('reviewSection'))
  }

  get item(): Item {
    return new Item(this.get('item'))
  }

  get itemList(): ItemList {
    return new ItemList(this.get('itemlist'))
  }

  get itemDetail(): ItemDetail {
    return new ItemDetail(this.get('itemDetail'))
  }

  get cart(): Cart {
    return new Cart(this.get('cart'))
  }

  get orderHistory(): OrderHistory {
    return new OrderHistory(this.get('orderHistory'))
  }

  get copyright() {
    return this.get('copyright')
  }

  get orderConfirm() {
    return {
      payment: {
        highlightCreditCard: this.get('orderConfirm').payment.highlightCreditCard,
      },
    }
  }

  get myPageLayoutDesign() {
    return {
      layoutType: this.get('myPageLayoutDesign').layoutType,
    }
  }

  get usualPower() {
    return {
      enabled: this.get('usualPower').enabled,
    }
  }

  get showPageLoader(): boolean {
    return this.get('showPageLoader')
  }

  get footer(): Footer {
    return new Footer(this.get('footer'))
  }

  get sideBar(): SideBar {
    return new SideBar(this.get('sidebar'))
  }

  get useCarat(): boolean {
    return this.get('useCarat')
  }

  get isGenreShown(): boolean {
    return this.get('isGenreShown')
  }

  get hostId(): string {
    return AppId.getHostId()
  }

  get brandGroup(): BrandGroupEntity | null {
    const group = this.json.brandGroup[this.hostId]

    return group || null
  }
  get brand(): BrandEntity | null {
    if (this.brandAppId === null) {
      return null
    }

    return _.get(
      this.brandGroup,
      `brand.${this.brandAppId}`,
      null
    )
  }
  get brandName(): string | null {
    if (this.brandIdentifier === null) {
      return null
    }

    return this.brandIdentifier.length > 0 ? this.brandIdentifier : null
  }

  get default(): BrandGroupEntity {
    return this.json.default
  }
  get layer(): Layer {
    return new Layer(this.get('layer'))
  }

  get authentication() {
    return {
      sms: this.get('authentication').sms,
    }
  }

  get colorFamily(): ColorFamily {
    return this.get('colorFamily')
  }

  get filterIsExclusive(): FilterIsExclusive {
    return this.get('filterIsExclusive')
  }

  get mailDomain(): string {
    return this.get('mailDomain')
  }

  get paidMembership(): PaidMembership {
    return this.get('paidMembership')
  }

  get bundleRecommendation(): BundleRecommendation {
    return new BundleRecommendation(this.get('bundleRecommendation'))
  }

  get discount(): Discount {
    return this.get('discount')
  }

  fixShippingFee(totalPrice: number): ShippingFee {
    const raw = this.get('fixShippingFee')

    return new BrandShippingFeeBuilder(raw, createCurrentTime()).build(totalPrice)
  }

  get shipmentSuspendNotification(): ShipmentSuspendNotificationConfig {
    return new ShipmentSuspendNotificationConfig(
      this.get('shipmentSuspendNotification')
    )
  }

  get addressPlaceholder(): Config['addressPlaceholder'] {
    return this.get('addressPlaceholder')
  }

  private get<T extends keyof Config>(path: T): Config[T] {
    /*
     * 優先度はブランドの設定 > タウンの設定 > デフォルトの設定
     */
    const brandConfig = _.get(
      this.brand,
      `config.${path}`,
      null
    )

    if (brandConfig !== null) {
      return brandConfig
    }

    const brandGroupConfig = _.get(
      this.brandGroup,
      `config.${path}`,
      null
    )
    if (brandGroupConfig !== null) {
      return brandGroupConfig
    }

    return this.default.config[path]
  }

  private getGroupAttribute<T extends keyof BrandGroupEntity>(key: T): BrandGroupEntity[T] {
    return _.get<BrandGroupEntity, T, BrandGroupEntity[T]>(
      this.brandGroup,
      key,
      this.default[key]
    )
  }
}
